import { ReactElement, useEffect, useState } from 'react';
import { VStack, Text, DataItem } from '@cad-arc';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { LastUpdateTime } from './last-update-time';
import { DELAY_FOR_UNIT_UPDATE, DELAY_FOR_USER_UPDATE } from '../../configuration';
import { CommonSize } from '../../../common/helpers/utils';
import { convertToMaritime } from '../../../location/helpers';
import {
    FormatType,
    MaritimeFormatsButtonsSection,
} from '../../../common/components/maritime-formats-buttons-section';
import { useApplicationType } from '../../../common/hooks/use-application-type';
import { MaritimeFormatsRadioButtonsSection } from '../../../common/components/maritime-formats-radio-buttons-section';

export const NO_COORDINATES = '--';

export function LocationDegreesDataItem({
    size,
    isUserLocation,
    isGpsLocation,
    isGpsLocationValid,
    isGeoLocationValid,

    gpsLocationLatitude,
    gpsLocationLongitude,
    gpsLocationTimestamp,

    geoLocationLatitude,
    geoLocationLongitude,
}: {
    size?: CommonSize;
    isUserLocation?: boolean;
    isGpsLocation?: boolean;
    isGpsLocationValid?: boolean;
    isGeoLocationValid?: boolean;

    gpsLocationLatitude?: number;
    gpsLocationLongitude?: number;
    gpsLocationTimestamp?: number;

    geoLocationLatitude?: number;
    geoLocationLongitude?: number;
}): ReactElement {
    const { maritimeCoordinatesEnabled } = useEnabledFeatures();
    const { isFirstResponder } = useApplicationType();
    const [formatType, setFormatType] = useState<FormatType>('DD');
    const [geoCoordinates, setGeoCoordinates] = useState(NO_COORDINATES);
    const [gpsCoordinates, setGpsCoordinates] = useState(NO_COORDINATES);

    useEffect(() => {
        switch (formatType) {
            case 'DDM':
            case 'DMS': {
                if (maritimeCoordinatesEnabled) {
                    setGeoCoordinates(
                        isGeoLocationValid
                            ? `${convertToMaritime({
                                  format: formatType,
                                  coordinate: geoLocationLatitude,
                                  isLatitude: true,
                              })}, ${convertToMaritime({
                                  format: formatType,
                                  coordinate: geoLocationLongitude,
                                  isLatitude: false,
                              })}`
                            : NO_COORDINATES
                    );

                    setGpsCoordinates(
                        isGpsLocationValid
                            ? `${convertToMaritime({
                                  format: formatType,
                                  coordinate: gpsLocationLatitude,
                                  isLatitude: true,
                              })}, ${convertToMaritime({
                                  format: formatType,
                                  coordinate: gpsLocationLongitude,
                                  isLatitude: false,
                              })}`
                            : NO_COORDINATES
                    );
                }
                return;
            }
            case 'DD': {
                setGeoCoordinates(
                    isGeoLocationValid
                        ? `${geoLocationLatitude}, ${geoLocationLongitude}`
                        : NO_COORDINATES
                );

                setGpsCoordinates(
                    isGpsLocationValid
                        ? `${gpsLocationLatitude}, ${gpsLocationLongitude}`
                        : NO_COORDINATES
                );

                return;
            }
        }
    }, [
        formatType,
        isGeoLocationValid,
        isGpsLocationValid,
        maritimeCoordinatesEnabled,
        gpsLocationLatitude,
        gpsLocationLongitude,
        geoLocationLatitude,
        geoLocationLongitude,
    ]);

    return (
        <DataItem
            dataKey={<Text fontSize={size}>GPS</Text>}
            variant="stacked"
            style={{ wordBreak: 'break-word' }}
        >
            <VStack width="100%" alignItems="start">
                <Text data-testid="MAP_LOCATION_GPS_COORDINATES" fontSize={size}>
                    {isGpsLocation ? gpsCoordinates : geoCoordinates}{' '}
                    {isGpsLocation && gpsLocationTimestamp && (
                        <LastUpdateTime
                            // this LastUpdateTime is for gps location only.  For geo location, update time is handled by EntityAddress component.
                            timeUpdateKey={gpsCoordinates}
                            lastTimestamp={gpsLocationTimestamp}
                            delay={isUserLocation ? DELAY_FOR_USER_UPDATE : DELAY_FOR_UNIT_UPDATE}
                            size={size}
                        />
                    )}
                </Text>
                {maritimeCoordinatesEnabled &&
                    (isFirstResponder ? (
                        <MaritimeFormatsButtonsSection
                            activeFormat={formatType}
                            setActiveFormat={setFormatType}
                        />
                    ) : (
                        <MaritimeFormatsRadioButtonsSection
                            activeFormat={formatType}
                            setActiveFormat={setFormatType}
                        />
                    ))}
            </VStack>
        </DataItem>
    );
}
